import React from "react";

const reloadStorageKey = "captyn:chunk-reload-at";
const reloadCooldown = 5 * 60_000;

export default function lazyImport<Component extends React.ComponentType<any>>(
  importPromise: () => Promise<{ default: Component }>,
  errCallback: () => void = errorCallback
) {
  const LazyComponent = React.lazy(() => {
    return componentLoader(importPromise, 3).catch((error) => {
      if (isChunkLoadError(error)) {
        errCallback();
      }
      throw error;
    });
  });

  // Expose the raw promise function so we can manually trigger chunk downloads
  return Object.assign(LazyComponent, { preload: importPromise });
}

const errorCallback = () => {
  if (window.navigator.onLine === false) {
    return;
  }

  try {
    const now = Date.now();
    const lastReload = window.sessionStorage.getItem(reloadStorageKey);

    if (lastReload !== null && now - Number(lastReload) < reloadCooldown) {
      return;
    }

    window.sessionStorage.setItem(reloadStorageKey, String(now));
  } catch {
    return;
  }

  window.location.reload();
};

function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|'text\/(?:plain|html)' is not a valid JavaScript MIME type|Unable to preload CSS for|Loading (?:CSS )?chunk .+ failed/i.test(
      error.message
    )
  );
}

async function componentLoader<Module>(
  lazyComponent: () => Promise<Module>,
  attemptsLeft: number
): Promise<Module> {
  try {
    return await lazyComponent();
  } catch (error) {
    if (!isChunkLoadError(error) || attemptsLeft === 1) {
      throw error;
    }

    await new Promise<void>((resolve) => setTimeout(resolve, 5000));
    return componentLoader(lazyComponent, attemptsLeft - 1);
  }
}
